// ===================================================================
// Battle Rhythm -- Activation Detail
// Shows op period info, event timeline, countdown logic, event CRUD
// ===================================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Text, Paper, Group, Stack, Badge, Button, Select, Modal,
  TextInput, Textarea, NumberInput, Checkbox, Loader, Center,
  Menu, ActionIcon,
} from '@mantine/core';
import {
  IconArrowLeft, IconPlus, IconTemplate, IconLock,
  IconPresentation, IconDotsVertical, IconDownload,
  IconUsers, IconFileText, IconArrowsExchange, IconPhone,
  IconTruck, IconMap, IconFlag,
  IconPlayerPlay, IconCheck, IconX, IconPencil, IconTrash,
  IconRepeat, IconBolt, IconMapPin, IconUser, IconClock,
  IconNotes,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useBattleRhythmStore } from '../../store/battlerhythmStore';
import type { BREvent, TemplateEvent } from '../../store/battlerhythmStore';
import { useAuthStore } from '../../store/authStore';

// -- Category constants -----------------------------------------------

const EVENT_CATEGORIES: Record<string, { label: string; color: string }> = {
  briefing:  { label: 'Briefing',     color: '#4a82c4' },
  report:    { label: 'Report Due',   color: '#d97706' },
  shift:     { label: 'Shift Change', color: '#8b5cf6' },
  external:  { label: 'External',     color: '#059669' },
  logistics: { label: 'Logistics',    color: '#ea580c' },
  planning:  { label: 'Planning',     color: '#0891b2' },
  other:     { label: 'Other',        color: '#627d98' },
};

const CATEGORY_ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
  briefing:  IconUsers,
  report:    IconFileText,
  shift:     IconArrowsExchange,
  external:  IconPhone,
  logistics: IconTruck,
  planning:  IconMap,
  other:     IconFlag,
};

const STATUS_COLORS: Record<string, string> = {
  upcoming:      'blue',
  'in-progress': 'yellow',
  completed:     'green',
  skipped:       'gray',
};

const CATEGORY_OPTIONS = Object.entries(EVENT_CATEGORIES).map(([value, { label }]) => ({
  value,
  label,
}));

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'skipped', label: 'Skipped' },
];

const CATEGORY_FILTER_OPTIONS = [
  { value: 'all', label: 'All Categories' },
  ...CATEGORY_OPTIONS,
];

// -- Countdown logic (ported exactly) ---------------------------------

interface CountdownResult {
  label: string;
  kind: 'done' | 'future' | 'now' | 'active';
}

function parseTime(t: string): [number, number] {
  if (!t) return [0, 0];
  const s = t.trim();
  if (s.includes('T')) {
    const d = dayjs(s);
    if (d.isValid()) return [d.hour(), d.minute()];
  }
  // Military time without colon: "0700", "900"
  if (/^\d{3,4}$/.test(s)) {
    const p = s.padStart(4, '0');
    return [parseInt(p.slice(0, 2), 10), parseInt(p.slice(2, 4), 10)];
  }
  const parts = s.split(':').map(Number);
  return [isNaN(parts[0]) ? 0 : parts[0], isNaN(parts[1]) ? 0 : parts[1]];
}

function getCountdown(scheduledTime: string, status: string): CountdownResult {
  if (status === 'completed' || status === 'skipped') {
    return { label: 'COMPLETE', kind: 'done' };
  }
  if (status === 'in-progress') {
    const [hh, mm] = parseTime(scheduledTime);
    const scheduled = dayjs().hour(hh).minute(mm).second(0);
    const elapsed = dayjs().diff(scheduled, 'minute');
    return { label: `IN PROGRESS (${Math.max(0, elapsed)}m elapsed)`, kind: 'active' };
  }

  const [hh, mm] = parseTime(scheduledTime);
  const scheduled = dayjs().hour(hh).minute(mm).second(0);
  const diffMin = scheduled.diff(dayjs(), 'minute');

  if (diffMin > 60) {
    const hours = Math.floor(diffMin / 60);
    const mins = diffMin % 60;
    return { label: `in ${hours}h ${mins}m`, kind: 'future' };
  }
  if (diffMin > 2) {
    return { label: `in ${diffMin}m`, kind: 'future' };
  }
  if (diffMin >= -2) {
    return { label: 'NOW', kind: 'now' };
  }
  // Past
  const ago = Math.abs(diffMin);
  return { label: `${ago}m ago`, kind: 'future' };
}

// -- Empty event form state -------------------------------------------

interface EventForm {
  title: string;
  category: string;
  scheduledTime: string;
  durationMinutes: number;
  location: string;
  responsible: string;
  notes: string;
  recurring: boolean;
}

const EMPTY_FORM: EventForm = {
  title: '',
  category: 'briefing',
  scheduledTime: '08:00',
  durationMinutes: 30,
  location: '',
  responsible: '',
  notes: '',
  recurring: false,
};

// =====================================================================
// Component
// =====================================================================

export default function ActivationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const {
    activations, events, templates, loading,
    loadActivations, loadEvents, loadTemplates,
    createEvent, updateEvent, deleteEvent, closeActivation,
  } = useBattleRhythmStore();

  // -- Local state ----------------------------------------------------

  const [catFilter, setCatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Event modal
  const [eventModal, setEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<BREvent | null>(null);
  const [form, setForm] = useState<EventForm>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  // Template modal
  const [templateModal, setTemplateModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  // Close confirmation
  const [confirmClose, setConfirmClose] = useState(false);

  // Countdown ticker
  const [tick, setTick] = useState(0);

  // -- Data loading ---------------------------------------------------

  const incidentId = activeIncident ? String(activeIncident.id) : '';

  useEffect(() => {
    if (incidentId) {
      loadActivations(incidentId);
      loadEvents(incidentId);
    }
    loadTemplates();
  }, [incidentId]);

  // Tick every 30 seconds to update countdowns
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);

  // -- Derived data ---------------------------------------------------

  const activation = useMemo(
    () => activations.find(a => a.id === Number(id)),
    [activations, id],
  );

  const isClosed = activation?.status === 'closed';

  // Events for this incident (all events belong to incident, not per-activation)
  const incidentEvents = useMemo(
    () => events.filter(e => String(e.incidentId) === incidentId),
    [events, incidentId],
  );

  // Filtered + sorted
  const filteredEvents = useMemo(() => {
    let list = incidentEvents;
    if (catFilter !== 'all') list = list.filter(e => e.category === catFilter);
    if (statusFilter !== 'all') list = list.filter(e => e.status === statusFilter);
    return [...list].sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentEvents, catFilter, statusFilter, tick]);

  // Op period display
  const opPeriodLabel = useMemo(() => {
    if (!activation) return '';
    const hours = Number(activation.opHours) || 12;
    const start = activation.opStart ? dayjs(activation.opStart) : null;
    if (!start || !start.isValid()) return `${hours}h Op Period`;
    const end = start.add(hours, 'hour');
    return `${hours}h Op Period: ${start.format('HH:mm')} \u2013 ${end.format('HH:mm')}`;
  }, [activation]);

  // -- Handlers -------------------------------------------------------

  const openAddEvent = useCallback(() => {
    setEditingEvent(null);
    setForm({ ...EMPTY_FORM });
    setEventModal(true);
  }, []);

  const openEditEvent = useCallback((evt: BREvent) => {
    setEditingEvent(evt);
    setForm({
      title: evt.title,
      category: evt.category,
      scheduledTime: evt.scheduledTime,
      durationMinutes: Number(evt.durationMinutes) || 30,
      location: evt.location,
      responsible: evt.responsible,
      notes: evt.notes,
      recurring: evt.recurring === 'true',
    });
    setEventModal(true);
  }, []);

  const handleSaveEvent = async () => {
    if (!form.title.trim() || !incidentId) return;
    setSaving(true);
    try {
      if (editingEvent) {
        await updateEvent(editingEvent.id, {
          title: form.title.trim(),
          category: form.category,
          scheduledTime: form.scheduledTime,
          durationMinutes: String(form.durationMinutes),
          location: form.location,
          responsible: form.responsible,
          notes: form.notes,
          recurring: form.recurring ? 'true' : 'false',
          incidentId,
        });
      } else {
        await createEvent({
          incidentId,
          title: form.title.trim(),
          category: form.category,
          scheduledTime: form.scheduledTime,
          durationMinutes: String(form.durationMinutes),
          location: form.location,
          responsible: form.responsible,
          notes: form.notes,
          recurring: form.recurring ? 'true' : 'false',
          adHoc: 'true',
          status: 'upcoming',
          creator: user?.fullName ?? '',
        });
      }
      setEventModal(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (evt: BREvent) => {
    await deleteEvent(evt.id, incidentId);
  };

  const handleStatusChange = async (evt: BREvent, newStatus: string) => {
    await updateEvent(evt.id, {
      status: newStatus,
      incidentId,
      ...(newStatus === 'completed' ? { completedBy: user?.fullName ?? '' } : {}),
    });
  };

  const handleCloseActivation = async () => {
    if (!activation) return;
    await closeActivation(activation.id);
    setConfirmClose(false);
  };

  const handleLoadTemplate = async () => {
    if (!selectedTemplateId || !incidentId) return;
    const tmpl = templates.find(t => String(t.id) === selectedTemplateId);
    if (!tmpl) return;
    setTemplateLoading(true);
    try {
      const parsed: TemplateEvent[] = JSON.parse(tmpl.events || '[]');
      for (const te of parsed) {
        await createEvent({
          incidentId,
          title: te.title,
          category: te.category,
          scheduledTime: te.scheduledTime,
          durationMinutes: String(te.durationMinutes),
          location: te.location || '',
          responsible: te.responsible || '',
          notes: te.notes || '',
          recurring: te.recurring ? 'true' : 'false',
          adHoc: 'false',
          status: 'upcoming',
          creator: user?.fullName ?? '',
        });
      }
      setTemplateModal(false);
      setSelectedTemplateId(null);
    } catch (err) {
      console.error('Failed to load template events:', err);
    } finally {
      setTemplateLoading(false);
    }
  };

  // -- Export CSV -----------------------------------------------------

  const handleExportCSV = () => {
    const rows = [
      ['Time', 'Title', 'Category', 'Duration (min)', 'Location', 'Responsible', 'Status', 'Recurring', 'Notes'],
      ...incidentEvents
        .sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime))
        .map(e => [
          e.scheduledTime,
          e.title,
          (EVENT_CATEGORIES[e.category] ?? EVENT_CATEGORIES.other).label,
          e.durationMinutes,
          e.location,
          e.responsible,
          e.status,
          e.recurring === 'true' ? 'Yes' : 'No',
          e.notes,
        ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `battle-rhythm-${activation?.org || 'events'}-${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // -- Export PDF -----------------------------------------------------

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const title = `Battle Rhythm — ${activation?.org || 'Activation'}`;
    const subtitle = activeIncident
      ? `${activeIncident.name} | ${opPeriodLabel} | Generated ${dayjs().format('MMM D, YYYY HH:mm')}`
      : `Generated ${dayjs().format('MMM D, YYYY HH:mm')}`;

    // Header
    doc.setFontSize(18);
    doc.setTextColor(44, 1, 214); // brand navy
    doc.text(title, 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(subtitle, 14, 26);

    const sorted = [...incidentEvents].sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));

    const tableRows = sorted.map(e => [
      e.scheduledTime,
      e.title,
      (EVENT_CATEGORIES[e.category] ?? EVENT_CATEGORIES.other).label,
      `${e.durationMinutes} min`,
      e.location || '—',
      e.responsible || '—',
      e.status.charAt(0).toUpperCase() + e.status.slice(1),
      e.recurring === 'true' ? 'Yes' : '',
      e.notes || '',
    ]);

    autoTable(doc, {
      startY: 32,
      head: [['Time', 'Event', 'Category', 'Duration', 'Location', 'Responsible', 'Status', 'Recurring', 'Notes']],
      body: tableRows,
      headStyles: { fillColor: [44, 1, 214], textColor: 255, fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 40 },
        2: { cellWidth: 22 },
        3: { cellWidth: 18 },
        4: { cellWidth: 30 },
        5: { cellWidth: 28 },
        6: { cellWidth: 22 },
        7: { cellWidth: 16 },
        8: { cellWidth: 'auto' },
      },
      alternateRowStyles: { fillColor: [245, 245, 255] },
      margin: { left: 14, right: 14 },
    });

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text('OpenGov EMC — Battle Rhythm', 14, doc.internal.pageSize.height - 8);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 8);
    }

    doc.save(`battle-rhythm-${activation?.org || 'events'}-${dayjs().format('YYYY-MM-DD')}.pdf`);
  };

  // -- Render guards --------------------------------------------------

  if (loading && !activation) {
    return <Center p="xl"><Loader /></Center>;
  }

  if (!activation) {
    return (
      <Paper p="xl" radius="sm" withBorder>
        <Text ta="center" c="dimmed">Activation not found.</Text>
        <Center mt="md">
          <Button variant="subtle" leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate('/battlerhythm')}>
            Back to Battle Rhythm
          </Button>
        </Center>
      </Paper>
    );
  }

  // -- Render ---------------------------------------------------------

  return (
    <div>
      {/* Header */}
      <Group gap="xs" mb="xs">
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/battlerhythm')}>
          Battle Rhythm
        </Button>
      </Group>

      <Group justify="space-between" mb="xs" wrap="wrap">
        <Group gap="sm">
          <Text fw={700} fz={24}>{activation.org || 'Activation'}</Text>
          <Badge size="lg" color={isClosed ? 'gray' : 'green'} variant="filled" tt="capitalize">
            {activation.status}
          </Badge>
        </Group>

        {!isClosed && (
          <Group gap="xs">
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openAddEvent}>
              Add Event
            </Button>
            <Button size="xs" variant="light" leftSection={<IconTemplate size={14} />}
              onClick={() => setTemplateModal(true)}>
              Add from Template
            </Button>
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
              onClick={handleExportCSV}>
              CSV
            </Button>
            <Button size="xs" variant="light" leftSection={<IconFileText size={14} />}
              onClick={handleExportPDF}>
              PDF
            </Button>
            <Button size="xs" variant="light" color="red" leftSection={<IconLock size={14} />}
              onClick={() => setConfirmClose(true)}>
              Close Activation
            </Button>
            <Button size="xs" variant="light" color="violet"
              leftSection={<IconPresentation size={14} />}
              onClick={() => navigate('/battlerhythm/display')}>
              Display Board
            </Button>
          </Group>
        )}
      </Group>

      <Group gap="sm" mb="md">
        <Text size="sm" c="dimmed">{opPeriodLabel}</Text>
        {isClosed && (
          <>
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
              onClick={handleExportCSV}>
              CSV
            </Button>
            <Button size="xs" variant="light" leftSection={<IconFileText size={14} />}
              onClick={handleExportPDF}>
              PDF
            </Button>
          </>
        )}
      </Group>

      {isClosed && (
        <Paper p="md" radius="sm" withBorder mb="md"
          style={{ background: '#f8f8f8', borderColor: '#ccc' }}>
          <Group gap="xs">
            <IconLock size={16} color="#888" />
            <Text size="sm" c="dimmed">
              This activation is closed. Events are read-only.
              {activation.closedAt && ` Closed ${dayjs(activation.closedAt).format('MMM D, YYYY HH:mm')}.`}
            </Text>
          </Group>
        </Paper>
      )}

      {/* Filters */}
      <Group gap="sm" mb="md">
        <Select size="xs" data={CATEGORY_FILTER_OPTIONS} value={catFilter}
          onChange={v => setCatFilter(v ?? 'all')} style={{ width: 180 }}
          placeholder="Category" />
        <Select size="xs" data={STATUS_FILTER_OPTIONS} value={statusFilter}
          onChange={v => setStatusFilter(v ?? 'all')} style={{ width: 180 }}
          placeholder="Status" />
        <Text size="xs" c="dimmed" ml="auto">{filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}</Text>
      </Group>

      {/* Event Timeline */}
      {filteredEvents.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">
            {incidentEvents.length === 0
              ? 'No events yet. Add an event or load from a template.'
              : 'No events match the current filters.'}
          </Text>
        </Paper>
      ) : (
        <Stack gap="sm">
          {filteredEvents.map(evt => (
            <EventCard
              key={evt.id}
              event={evt}
              isClosed={isClosed}
              tick={tick}
              onEdit={() => openEditEvent(evt)}
              onDelete={() => handleDeleteEvent(evt)}
              onStatusChange={(status) => handleStatusChange(evt, status)}
            />
          ))}
        </Stack>
      )}

      {/* Add / Edit Event Modal */}
      <Modal opened={eventModal} onClose={() => setEventModal(false)}
        title={editingEvent ? 'Edit Event' : 'Add Event'} size="md">
        <Stack gap="sm">
          <TextInput label="Title" placeholder="Event title" required
            value={form.title} onChange={e => setForm(f => ({ ...f, title: e.currentTarget.value }))} />
          <Select label="Category" data={CATEGORY_OPTIONS}
            value={form.category} onChange={v => setForm(f => ({ ...f, category: v ?? 'other' }))} />
          <Group grow>
            <TextInput label="Time" type="time"
              value={form.scheduledTime}
              onChange={e => setForm(f => ({ ...f, scheduledTime: e.currentTarget.value }))} />
            <NumberInput label="Duration (min)" min={5} max={480}
              value={form.durationMinutes}
              onChange={v => setForm(f => ({ ...f, durationMinutes: Number(v) || 30 }))} />
          </Group>
          <TextInput label="Location" placeholder="Room / area"
            value={form.location} onChange={e => setForm(f => ({ ...f, location: e.currentTarget.value }))} />
          <TextInput label="Responsible" placeholder="Person or role"
            value={form.responsible} onChange={e => setForm(f => ({ ...f, responsible: e.currentTarget.value }))} />
          <Textarea label="Notes" placeholder="Additional details..."
            value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.currentTarget.value }))}
            minRows={2} autosize />
          <Checkbox label="Recurring event" checked={form.recurring}
            onChange={e => setForm(f => ({ ...f, recurring: e.currentTarget.checked }))} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setEventModal(false)}>Cancel</Button>
            <Button onClick={handleSaveEvent} loading={saving} disabled={!form.title.trim()}>
              {editingEvent ? 'Save Changes' : 'Add Event'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Add from Template Modal */}
      <Modal opened={templateModal} onClose={() => setTemplateModal(false)}
        title="Add Events from Template" size="sm">
        <Stack gap="sm">
          <Select label="Template" placeholder="Select a template"
            data={templates.map(t => ({ value: String(t.id), label: t.name }))}
            value={selectedTemplateId}
            onChange={v => setSelectedTemplateId(v)} />
          {selectedTemplateId && (() => {
            const tmpl = templates.find(t => String(t.id) === selectedTemplateId);
            if (!tmpl) return null;
            let count = 0;
            try { count = JSON.parse(tmpl.events || '[]').length; } catch { /* noop */ }
            return (
              <Text size="xs" c="dimmed">
                {tmpl.description && `${tmpl.description} -- `}
                {count} event{count !== 1 ? 's' : ''} will be added.
              </Text>
            );
          })()}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setTemplateModal(false)}>Cancel</Button>
            <Button onClick={handleLoadTemplate} loading={templateLoading}
              disabled={!selectedTemplateId}>
              Load Events
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Close Activation Confirm */}
      <Modal opened={confirmClose} onClose={() => setConfirmClose(false)}
        title="Close Activation" size="sm">
        <Stack gap="sm">
          <Text size="sm">
            Are you sure you want to close this activation? Events will become read-only.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setConfirmClose(false)}>Cancel</Button>
            <Button color="red" onClick={handleCloseActivation}>Close Activation</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}

// =====================================================================
// EventCard sub-component
// =====================================================================

interface EventCardProps {
  event: BREvent;
  isClosed: boolean;
  tick: number;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (status: string) => void;
}

function EventCard({ event, isClosed, tick, onEdit, onDelete, onStatusChange }: EventCardProps) {
  const cat = EVENT_CATEGORIES[event.category] ?? EVENT_CATEGORIES.other;
  const CatIcon = CATEGORY_ICONS[event.category] ?? IconFlag;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const countdown = useMemo(() => getCountdown(event.scheduledTime, event.status), [event.scheduledTime, event.status, tick]);

  const countdownColor =
    countdown.kind === 'done' ? '#07963F' :
    countdown.kind === 'active' ? '#d97706' :
    countdown.kind === 'now' ? 'var(--color-error)' :
    '#627d98';

  return (
    <Paper p="sm" radius="sm" withBorder
      style={{
        borderLeft: `4px solid ${cat.color}`,
        opacity: event.status === 'skipped' ? 0.6 : 1,
      }}>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Group gap="sm" wrap="nowrap" align="flex-start" style={{ flex: 1 }}>
          {/* Category icon */}
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: cat.color + '18', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <CatIcon size={18} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Title row */}
            <Group gap="xs" mb={4} wrap="wrap">
              <Text fw={600} size="sm"
                td={event.status === 'skipped' ? 'line-through' : undefined}>
                {event.title}
              </Text>
              <Badge size="xs" color={STATUS_COLORS[event.status] ?? 'gray'} tt="capitalize">
                {event.status}
              </Badge>
              {event.recurring === 'true' && (
                <Badge size="xs" variant="outline" color="violet"
                  leftSection={<IconRepeat size={10} />}>
                  Recurring
                </Badge>
              )}
              {event.adHoc === 'true' && (
                <Badge size="xs" variant="outline" color="orange"
                  leftSection={<IconBolt size={10} />}>
                  Ad Hoc
                </Badge>
              )}
            </Group>

            {/* Time + countdown */}
            <Group gap="sm" mb={4}>
              <Group gap={4}>
                <IconClock size={12} color="#888" />
                <Text size="xs" c="dimmed">{event.scheduledTime}</Text>
              </Group>
              <Text size="xs" fw={600} style={{ color: countdownColor }}>
                {countdown.label}
              </Text>
              {event.durationMinutes && event.durationMinutes !== '0' && (
                <Text size="xs" c="dimmed">{event.durationMinutes} min</Text>
              )}
            </Group>

            {/* Details row */}
            <Group gap="sm" wrap="wrap">
              {event.location && (
                <Group gap={4}>
                  <IconMapPin size={12} color="#888" />
                  <Text size="xs" c="dimmed">{event.location}</Text>
                </Group>
              )}
              {event.responsible && (
                <Group gap={4}>
                  <IconUser size={12} color="#888" />
                  <Text size="xs" c="dimmed">{event.responsible}</Text>
                </Group>
              )}
            </Group>

            {/* Notes */}
            {event.notes && (
              <Group gap={4} mt={4}>
                <IconNotes size={12} color="#888" />
                <Text size="xs" c="dimmed" lineClamp={2}>{event.notes}</Text>
              </Group>
            )}
          </div>
        </Group>

        {/* Action menu */}
        {!isClosed && (
          <Menu position="bottom-end" shadow="md" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="sm">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {event.status !== 'in-progress' && event.status !== 'completed' && event.status !== 'skipped' && (
                <Menu.Item leftSection={<IconPlayerPlay size={14} />}
                  onClick={() => onStatusChange('in-progress')}>
                  Mark In Progress
                </Menu.Item>
              )}
              {event.status !== 'completed' && (
                <Menu.Item leftSection={<IconCheck size={14} />} color="green"
                  onClick={() => onStatusChange('completed')}>
                  Mark Complete
                </Menu.Item>
              )}
              {event.status !== 'skipped' && event.status !== 'completed' && (
                <Menu.Item leftSection={<IconX size={14} />} color="gray"
                  onClick={() => onStatusChange('skipped')}>
                  Mark Skipped
                </Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item leftSection={<IconPencil size={14} />} onClick={onEdit}>
                Edit
              </Menu.Item>
              <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={onDelete}>
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
    </Paper>
  );
}
